import React, {createContext, useContext, useMemo, useState, useEffect, createRef} from 'react';
import { createTheme, ThemeOptions, Theme, ThemeProvider as ThemeMUIProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CssBaseline from '@mui/material/CssBaseline';

interface IContext {
  themeScheme: string | 'dark' | 'light' | 'system';
  setThemeScheme(mode: 'dark' | 'light' | 'system'): void;
  theme: Theme;
  changeThemeScheme(mode: 'dark' | 'light' | 'system'): void;
  themeMode?: string | 'dark' | 'light';
  setConfigTheme(theme: ThemeOptions): void;
  isLoadingStartApp: boolean;
  setisLoadingStartApp(isLoadingStartApp: boolean): void
}

export const ThemeContext = createContext({} as IContext);
export let themeContextState = createRef<IContext>().current;

export default function ThemeProvider({children}: {children: React.ReactNode}) {
  const [configTheme, setConfigTheme] = useState<ThemeOptions>({});
  const [isLoadingStartApp, setisLoadingStartApp] = useState(true);
  const [themeScheme, setThemeScheme] = useState<string | 'dark' | 'light' | 'system'>('light');
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const systemMode = 'light';

  const changeThemeScheme = (mode: string) => {
    localStorage.setItem('themeScheme', mode);
    setThemeScheme(mode);
  }

  const theme = createTheme({
    ...configTheme
  } as ThemeOptions);

  const themeMode = themeScheme === 'system' 
    ? systemMode 
    : themeScheme;

  const value = useMemo(() => ({
    theme,
    themeScheme,
    setThemeScheme,
    changeThemeScheme,
    themeMode,
    setConfigTheme,
    isLoadingStartApp,
    setisLoadingStartApp
  }), [theme, themeScheme, themeMode, isLoadingStartApp]);

  themeContextState = value;

  useEffect(() => {
    const themeSchemeMode = localStorage.getItem('themeScheme');
    setThemeScheme(themeSchemeMode || systemMode);
  }, [prefersDarkMode])

  return (
    <ThemeMUIProvider theme={theme}>
      <CssBaseline />
      <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
    </ThemeMUIProvider>
  );
}

export function useTheme() {
  const context: IContext = useContext(ThemeContext);
  return context;
}